import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  FormGroup,
  FormControlLabel,
  Switch,
  FormHelperText,
  Button,
} from '@material-ui/core';

import { useLazyRequest } from '../../hooks';
import { createKriptonian } from '../../api';
import { getHelperTextField, hasError } from '../../utils';
import { getApiErroMessage } from '../../utils/helpers';

import ButtonWithCirculaProgress from '../ButtonWithCirculaProgress';

import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const CreateKriptonianModal = ({ open = false, onClose, onCreateSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingCreateKriptonian, , executeCreateKriptonian] = useLazyRequest(
    {
      request: createKriptonian,
      withPostSuccess: () => {
        enqueueSnackbar('El kriptonian fue creado correctamente', {
          variant: 'success',
        });

        onClose();

        onCreateSuccess?.();
      },
      withPostFailure: (err) => {
        enqueueSnackbar(getApiErroMessage(err?.errorData), {
          variant: 'error',
        });
      },
    },
  );

  const onSubmit = useCallback(
    (values) => {
      executeCreateKriptonian({
        email: values[FIELDS_NAME.EMAIL],
        alias_external_operations: values[FIELDS_NAME.ALIAS],
        allow_k2p_to_qr: values[FIELDS_NAME.ACTIVE_QR],
        allow_k2p_to_external_cash_out: values[FIELDS_NAME.ACTIVE_CASHOUT],
      });
    },
    [executeCreateKriptonian],
  );

  const {
    values,
    errors,
    touched,
    getFieldProps,
    submitForm,
    resetForm,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        resetForm();
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="xs" open={open}>
      <DialogTitle>Crear Kriptonian</DialogTitle>

      <DialogContent>
        <Grid item container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="Email"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              autoComplete="off"
              error={hasError(errors, touched, FIELDS_NAME.EMAIL)}
              helperText={getHelperTextField(
                errors,
                touched,
                FIELDS_NAME.EMAIL,
              )}
              {...getFieldProps(FIELDS_NAME.EMAIL)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Alias"
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              fullWidth
              autoComplete="off"
              error={hasError(errors, touched, FIELDS_NAME.ALIAS)}
              // eslint-disable-next-line max-len
              helperText="Debe tener entre 6 y 20 caracteres y solo puede contener letras (A-Z, a-z) y guion bajo (_)."
              {...getFieldProps(FIELDS_NAME.ALIAS)}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormGroup row sx={{ alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      {...getFieldProps(FIELDS_NAME.ACTIVE_QR)}
                      checked={values[FIELDS_NAME.ACTIVE_QR]}
                      color="primary"
                    />
                  }
                  label="QR?"
                />
              </FormGroup>

              <FormHelperText>
                Los anuncios de QR tienen un precio dinamico de 2.5%
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl>
              <FormGroup row sx={{ alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      {...getFieldProps(FIELDS_NAME.ACTIVE_CASHOUT)}
                      checked={values[FIELDS_NAME.ACTIVE_CASHOUT]}
                      color="primary"
                    />
                  }
                  label="CashOut?"
                />
              </FormGroup>

              <FormHelperText>
                Los anuncios de CashOut tienen un precio dinamico de 1%
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" variant="outlined">
          Cancelar
        </Button>

        <ButtonWithCirculaProgress
          variant="outlined"
          color="primary"
          label="Crear"
          gutters={false}
          handleButtonClick={submitForm}
          loading={loadingCreateKriptonian}
        />
      </DialogActions>
    </Dialog>
  );
};

CreateKriptonianModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
};

export default CreateKriptonianModal;
